import { ASSISTANT_PANEL_MIN_SIZE } from "./constants";
import {
  IAssistantPanelPosition,
  IAssistantPanelObstruction,
  IAssistantPanelViewport,
  IAssistantPanelSize,
  IAssistantPanelResizeFrame,
} from "./types";

export function getAssistantPanelPosition(
  panel: DOMRect,
  overlays: DOMRect[],
  viewport: IAssistantPanelViewport,
  gap: number,
  preferredPosition?: IAssistantPanelPosition,
  highlights: readonly IAssistantPanelObstruction[] = []
): IAssistantPanelPosition | undefined {
  const right = Math.max(gap, viewport.width - panel.width - gap);
  const bottom = Math.max(gap, viewport.height - panel.height - gap);
  const candidates = [
    ...(preferredPosition ? [preferredPosition] : []),
    { left: right, top: bottom },
    { left: gap, top: bottom },
    { left: right, top: gap },
    { left: gap, top: gap },
  ];

  const overlap = (
    { left, top }: IAssistantPanelPosition,
    obstructions: readonly IAssistantPanelObstruction[]
  ) =>
    obstructions.reduce((area, popup) => {
      const width = Math.max(
        0,
        Math.min(left + panel.width, popup.left + popup.width + gap) -
          Math.max(left, popup.left - gap)
      );
      const height = Math.max(
        0,
        Math.min(top + panel.height, popup.top + popup.height + gap) -
          Math.max(top, popup.top - gap)
      );
      return area + width * height;
    }, 0);

  let best = candidates[0];
  let bestOverlap = overlap(best, overlays);
  let bestHighlightOverlap = overlap(best, highlights);

  for (const candidate of candidates.slice(1)) {
    const candidateOverlap = overlap(candidate, overlays);
    const candidateHighlightOverlap = overlap(candidate, highlights);

    if (
      candidateHighlightOverlap < bestHighlightOverlap ||
      (candidateHighlightOverlap === bestHighlightOverlap &&
        candidateOverlap < bestOverlap)
    ) {
      best = candidate;
      bestOverlap = candidateOverlap;
      bestHighlightOverlap = candidateHighlightOverlap;
    }
  }

  return best === candidates[0] && !preferredPosition ? undefined : best;
}

export function isAssistantPanelSize(
  value: unknown
): value is IAssistantPanelSize {
  if (!value || typeof value !== "object") return false;

  const size = value as Record<string, unknown>;

  return (
    typeof size.width === "number" &&
    Number.isFinite(size.width) &&
    size.width > 0 &&
    typeof size.height === "number" &&
    Number.isFinite(size.height) &&
    size.height > 0
  );
}

export function clampAssistantPanelSize(
  size: IAssistantPanelSize,
  viewport: IAssistantPanelViewport,
  gap: number
): IAssistantPanelSize {
  const width = Math.max(0, viewport.width - gap * 2);
  const height = Math.max(0, viewport.height - gap * 2);

  return {
    width: Math.min(
      width,
      Math.max(ASSISTANT_PANEL_MIN_SIZE.width, size.width)
    ),
    height: Math.min(
      height,
      Math.max(ASSISTANT_PANEL_MIN_SIZE.height, size.height)
    ),
  };
}

export function getAssistantPanelResizeFrame(
  bounds: DOMRect,
  size: IAssistantPanelSize,
  gap: number
): IAssistantPanelResizeFrame {
  const next = clampAssistantPanelSize(
    size,
    { width: bounds.right + gap, height: bounds.bottom + gap },
    gap
  );

  return {
    size: next,
    position: {
      left: bounds.right - next.width,
      top: bounds.bottom - next.height,
    },
  };
}
